package com.arena_web.data.websocket

import com.arena_web.core.config.AppConfig
import com.arena_web.core.logger.getLogger
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.flow.MutableSharedFlow
import kotlinx.coroutines.flow.SharedFlow
import kotlinx.coroutines.flow.asSharedFlow
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.JsonPrimitive
import kotlinx.serialization.json.contentOrNull
import kotlinx.serialization.json.jsonArray
import kotlinx.serialization.json.jsonObject
import kotlinx.serialization.json.jsonPrimitive

/** Optional string field — `JsonNull` (whose `.content` is the literal string
 *  "null") and blank values both count as absent. */
private fun JsonObject.optString(key: String): String? =
    (this[key] as? JsonPrimitive)?.contentOrNull?.takeIf { it.isNotBlank() }

sealed class UserWsEvent {
    data class ChatListUpdate(
        val chatId: String,
        /** Display name of the message author + short preview — present on
         *  new-message updates so the UI can show a "who wrote what" banner. */
        val senderName: String? = null,
        val preview: String? = null,
        val senderAvatarUrl: String? = null,
        val senderCardAssetsBasePath: String? = null,
    ) : UserWsEvent()
    data class NewNotification(
        val title: String,
        val body: String? = null,
        val notificationType: String? = null,
        /** Actor visuals parsed from the notification's dataJson — same fields
         *  the notifications panel uses for its player-card avatar. */
        val actorAvatarUrl: String? = null,
        val actorCardAssetsBasePath: String? = null,
    ) : UserWsEvent()
    data object ActivityUpdate : UserWsEvent()
    /**
     * Header heart-badge signal — fires when someone LIKEd the caller or a
     * mutual like turned into a match. `kind` is either "LIKE" or "MATCH".
     */
    data class InteractionsUpdate(val kind: String, val actorUserId: String?) : UserWsEvent()
    data class UserOnline(val userId: String) : UserWsEvent()
    data class UserOffline(val userId: String) : UserWsEvent()
    data class OnlineUsersList(val userIds: Set<String>) : UserWsEvent()
    data object Connected : UserWsEvent()
    data object Disconnected : UserWsEvent()
}

class UserWebSocketClient {
    private val logger = getLogger<UserWebSocketClient>()
    private val json = Json { ignoreUnknownKeys = true; isLenient = true }
    private val scope = CoroutineScope(SupervisorJob() + Dispatchers.Main)

    private val socket = ManagedWebSocket(
        name = "User",
        scope = scope,
        buildUrl = { token -> "${AppConfig.wsUrl}/user?token=$token" },
        onText = { handleMessage(it) },
        onConnected = { _events.tryEmit(UserWsEvent.Connected) },
        onDisconnected = { _events.tryEmit(UserWsEvent.Disconnected) },
    )

    private val _events = MutableSharedFlow<UserWsEvent>(extraBufferCapacity = 64)
    val events: SharedFlow<UserWsEvent> = _events.asSharedFlow()

    fun connect() {
        socket.start()
    }

    /**
     * Tab-return hook — mirrors mobile's `ensureConnected()`: revive the
     * socket if it died while the tab was hidden or offline, where the
     * backoff budget may have been exhausted long before the user came back.
     * A healthy (or already retrying) connection is left untouched; a socket
     * the server refused with 4403 stays closed.
     */
    fun ensureConnected() {
        socket.ensureConnected()
    }

    fun disconnect() {
        socket.stop()
    }

    private fun handleMessage(text: String) {
        try {
            val jsonElement = json.parseToJsonElement(text)
            val obj = jsonElement.jsonObject
            val type = obj["type"]?.jsonPrimitive?.content ?: return
            val data = obj["data"]

            val event = when (type) {
                "CHAT_LIST_UPDATE" -> {
                    val obj = data?.jsonObject ?: return
                    val chatId = obj["chatId"]?.jsonPrimitive?.content ?: return
                    UserWsEvent.ChatListUpdate(
                        chatId = chatId,
                        senderName = obj.optString("senderName"),
                        preview = obj.optString("preview"),
                        senderAvatarUrl = obj.optString("senderAvatarUrl"),
                        senderCardAssetsBasePath = obj.optString("senderCardAssetsBasePath"),
                    )
                }
                "NEW_NOTIFICATION" -> {
                    val obj = data?.jsonObject
                    val title = obj?.optString("title")
                    // dataJson is a nested JSON string with the actor payload
                    // (same shape NotificationDto.parseDataJson handles).
                    val payload = obj?.optString("dataJson")
                        ?.let { raw ->
                            try {
                                json.parseToJsonElement(raw).jsonObject
                            } catch (_: Exception) {
                                null
                            }
                        }
                    if (title != null) {
                        UserWsEvent.NewNotification(
                            title = title,
                            body = obj?.optString("body"),
                            notificationType = obj?.optString("type"),
                            actorAvatarUrl = payload?.optString("actorAvatarUrl"),
                            actorCardAssetsBasePath = payload?.optString("actorCardAssetsBasePath"),
                        )
                    } else null
                }
                "ACTIVITY_UPDATE" -> UserWsEvent.ActivityUpdate
                "INTERACTIONS_UPDATE" -> {
                    val kind = data?.jsonObject?.get("kind")?.jsonPrimitive?.content ?: "LIKE"
                    val actorUserId = data?.jsonObject?.get("actorUserId")?.jsonPrimitive?.content
                    UserWsEvent.InteractionsUpdate(kind = kind, actorUserId = actorUserId)
                }
                "USER_ONLINE" -> {
                    val userId = data?.jsonObject?.get("userId")?.jsonPrimitive?.content ?: return
                    UserWsEvent.UserOnline(userId)
                }
                "USER_OFFLINE" -> {
                    val userId = data?.jsonObject?.get("userId")?.jsonPrimitive?.content ?: return
                    UserWsEvent.UserOffline(userId)
                }
                "ONLINE_USERS_LIST" -> {
                    val userIds = data?.jsonObject?.get("userIds")?.jsonArray
                        ?.mapNotNull { it.jsonPrimitive.content }
                        ?.toSet() ?: emptySet()
                    UserWsEvent.OnlineUsersList(userIds)
                }
                else -> {
                    logger.debug("Unknown User WS event type: $type")
                    null
                }
            }

            if (event != null) {
                _events.tryEmit(event)
            }
        } catch (e: Exception) {
            logger.error("Failed to parse User WS message: ${e.message}")
        }
    }

}
